import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { db } from "../../../backend/firebaseConfig";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import formatUKPhone from "../../AdviserSupport/ContactList/HelperFunctions/formatUKPhone";
import WorkHistoryGantt, {
  isEmploymentHistory,
  isCurrentlyEmployedAtFirm,
  toDate,
} from "./WorkHistoryGantt";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { ReactComponent as HelpIcon } from "../../../assets/icons/information.svg";
import OutlinedStatusButton, {
  OUTLINED_STATUS_BUTTON_COLORS,
} from "../../DesignSystem/Buttons/OutlinedStatusButtons";
import ButtonTextLink from "../../DesignSystem/Buttons/ButtonTextLink";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import WorkHistoryReferenceForm from "./WorkHistoryReferenceForm";
import PersonalReferenceForm from "./PersonalReferenceForm";
import TradeReferenceForm from "./TradeReferenceForm";
import AccountantReferenceForm from "./AccountantReferenceForm";
import EducationalReferenceForm from "./EducationalReferenceForm";
import ApplicantReferenceDetailsModal from "./ApplicantReferenceDetailsModal";
import HelpfulContactsModal from "./HelpfulContactsModal";
import {
  getApplicationReferenceEmailBlockReason,
  RECRUITMENT_REFERENCE_STATUSES,
  normalizeReferenceStatus,
} from "./constants/recruitment";
import "../../../css/Recruitment/ReferencingWorkspace.css";

const NEW_REFERENCE_TYPE_OPTIONS = [
  { id: "non-regulated", name: "Non-Regulated Role" },
  { id: "regulated", name: "Regulated Role" },
  { id: "personal", name: "Personal Reference" },
  { id: "trade", name: "Trade Reference" },
  { id: "accountant", name: "Accountant Reference" },
  { id: "educational", name: "Educational Reference" },
];

const getReferenceStatus = (value, fallbackId = "awaiting-adviser") => {
  const storedStatus = normalizeReferenceStatus(value || fallbackId);

  return (
    RECRUITMENT_REFERENCE_STATUSES.find(
      (status) =>
        status.id === storedStatus ||
        normalizeReferenceStatus(status.label) === storedStatus,
    ) || RECRUITMENT_REFERENCE_STATUSES[0]
  );
};

const startOfDay = (value) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const getDefaultWorkHistoryStatus = (history) => {
  const storedStatus = history?.referenceStatus || history?.statusOfReference;
  if (storedStatus) return getReferenceStatus(storedStatus);

  const today = startOfDay(new Date());
  const sixYearsAgo = new Date(
    today.getFullYear() - 6,
    today.getMonth(),
    today.getDate(),
  );
  const historyEnd = isCurrentlyEmployedAtFirm(history?.currentlyEmployedAtFirm)
    ? today
    : toDate(history?.to);

  return getReferenceStatus(
    undefined,
    historyEnd && startOfDay(historyEnd) < sixYearsAgo
      ? "action-required"
      : "awaiting-adviser",
  );
};

const hasContinuousSixYearHistory = (workHistory) => {
  const today = startOfDay(new Date());
  const coverageStart = new Date(
    today.getFullYear() - 6,
    today.getMonth(),
    today.getDate(),
  );
  const intervals = Object.values(workHistory)
    .filter((history) => isEmploymentHistory(history))
    .map((history) => {
      const parsedStart = toDate(history?.from);
      const parsedEnd = toDate(history?.to) || today;

      if (!parsedStart || !parsedEnd) return null;

      const intervalStart = startOfDay(parsedStart) < coverageStart
        ? coverageStart
        : startOfDay(parsedStart);
      const intervalEnd = startOfDay(parsedEnd) > today
        ? today
        : startOfDay(parsedEnd);

      return intervalEnd >= coverageStart && intervalStart <= today
        ? { start: intervalStart, end: intervalEnd }
        : null;
    })
    .filter(Boolean)
    .sort((first, second) => first.start - second.start);

  if (!intervals.length || intervals[0].start > coverageStart) return false;

  let coveredUntil = intervals[0].end;

  for (let index = 1; index < intervals.length; index += 1) {
    const nextCoveredDay = new Date(coveredUntil);
    nextCoveredDay.setDate(nextCoveredDay.getDate() + 1);

    if (intervals[index].start > nextCoveredDay) return false;
    if (intervals[index].end > coveredUntil) coveredUntil = intervals[index].end;
  }

  return coveredUntil >= today;
};

const ReferencingWorkspacePage = () => {
  const { adviserId } = useParams();
  const [applicant, setApplicant] = useState(null);
  const [adviserLifecycle, setAdviserLifecycle] = useState({});
  const [workHistory, setWorkHistory] = useState({});
  const [referenceDetails, setReferenceDetails] = useState({});
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [isGanttCollapsed, setIsGanttCollapsed] = useState(false);
  const [isAddingReference, setIsAddingReference] = useState(false);
  const [newReferenceType, setNewReferenceType] = useState("");
  const [newReferenceId, setNewReferenceId] = useState("");
  const [isApplicantDetailsOpen, setIsApplicantDetailsOpen] = useState(false);
  const [isHelpfulContactsOpen, setIsHelpfulContactsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    let recruitmentData = {};
    let personalDetailsData = {};
    const receivedSnapshots = new Set();

    setLoading(true);
    setError("");

    const markSnapshotReceived = (key) => {
      receivedSnapshots.add(key);
      if (active && receivedSnapshots.size === 5) setLoading(false);
    };
    const handleSnapshotError = (snapshotError) => {
      if (!active) return;
      setError(
        snapshotError?.message ||
          "The referencing application could not be loaded.",
      );
      setLoading(false);
    };
    const publishApplicant = () => {
      if (!active) return;
      setApplicant({ ...recruitmentData, ...personalDetailsData });
    };

    const unsubscribers = [
      onSnapshot(
        doc(db, "Advisers", adviserId),
        (snapshot) => {
          if (active) {
            setAdviserLifecycle(snapshot.exists() ? snapshot.data() : {});
          }
          markSnapshotReceived("adviser");
        },
        handleSnapshotError,
      ),
      onSnapshot(
        doc(db, "Recruitment", adviserId),
        (snapshot) => {
          if (!active) return;
          if (!snapshot.exists()) {
            setError("This referencing application could not be found.");
            setLoading(false);
            return;
          }
          recruitmentData = snapshot.data() || {};
          publishApplicant();
          markSnapshotReceived("recruitment");
        },
        handleSnapshotError,
      ),
      onSnapshot(
        doc(db, "Recruitment", adviserId, "Personal Details", "Data"),
        (snapshot) => {
          personalDetailsData = snapshot.exists() ? snapshot.data() : {};
          publishApplicant();
          markSnapshotReceived("personal-details");
        },
        handleSnapshotError,
      ),
      onSnapshot(
        doc(
          db,
          "Recruitment",
          adviserId,
          "Application Details",
          "Work History",
        ),
        (snapshot) => {
          if (active) {
            setWorkHistory(snapshot.exists() ? snapshot.data() : {});
          }
          markSnapshotReceived("work-history");
        },
        handleSnapshotError,
      ),
      onSnapshot(
        doc(
          db,
          "Recruitment",
          adviserId,
          "Application Details",
          "Accountant Details",
        ),
        (snapshot) => {
          if (active) {
            setReferenceDetails(snapshot.exists() ? snapshot.data() : {});
          }
          markSnapshotReceived("reference-details");
        },
        handleSnapshotError,
      ),
    ];

    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [adviserId]);

  const applicantName =
    applicant?.recruitmentUpdates?.[0]?.applicantName ||
    [
      applicant?.forename || applicant?.firstName,
      applicant?.surname || applicant?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    applicant?.displayName ||
    "Referencing application";
  const applicationEmailBlockedReason =
    getApplicationReferenceEmailBlockReason(adviserLifecycle) ||
    getApplicationReferenceEmailBlockReason(applicant || {});
  const referenceEmailGuardProps = {
    applicationEmailBlocked: Boolean(applicationEmailBlockedReason),
    applicationEmailBlockedReason,
  };
  const email =
    applicant?.emailAddress || applicant?.email || applicant?.personalEmail || "";
  const contactNumbers = [
    applicant?.mobileTelephone,
    applicant?.homeTelephone,
    applicant?.officeTelephone,
  ]
    .filter(Boolean)
    .filter(
      (number, index, numbers) =>
        numbers.findIndex(
          (candidate) =>
            String(candidate).replace(/\D/g, "") ===
            String(number).replace(/\D/g, ""),
        ) === index,
    );
  const nationalInsuranceNumber = applicant?.nationalInsuranceNumber || "";
  const referenceHistories = useMemo(
    () => {
      return (
      Object.entries(workHistory)
        .filter(([, history]) => isEmploymentHistory(history))
        .map(([id, history]) => {
          const status = getDefaultWorkHistoryStatus(history);
          const isEducationHistory =
            history?.referenceType === "educational" ||
            String(
              history?.currentEmploymentStatus ||
                history?.employmentStatus ||
                history?.typeOfEmployment ||
                "",
            )
              .toLowerCase()
              .replace(/[^a-z]/g, "") === "ineducation";

          return {
            id,
            kind: "work-history",
            data:
              history?.referenceStatus || history?.statusOfReference
                ? history
                : { ...history, referenceStatus: status.id },
            name: isEducationHistory
              ? "In Education"
              : history?.nameOfFirm || "Employment",
            role:
              isEducationHistory
                ? "Educational Reference"
                : history?.regulatedRole === true ||
                    String(history?.regulatedRole).toLowerCase() === "yes"
                  ? "Regulated Role"
                  : "Non-Regulated Role",
            start: toDate(history?.from),
            status,
          };
        })
        .sort((first, second) => (second.start || 0) - (first.start || 0))
      );
    },
    [workHistory],
  );
  const hasSixYearsEmploymentHistory = useMemo(
    () => hasContinuousSixYearHistory(workHistory),
    [workHistory],
  );
  const hasCurrentEmployment = useMemo(
    () =>
      Object.values(workHistory).some(
        (history) =>
          isEmploymentHistory(history) &&
          isCurrentlyEmployedAtFirm(history?.currentlyEmployedAtFirm),
      ),
    [workHistory],
  );
  const additionalReferenceCards = useMemo(() => {
    const cards = [];
    const accountantName = [
      referenceDetails?.title,
      referenceDetails?.initials,
      referenceDetails?.surname,
    ]
      .filter(Boolean)
      .join(" ");

    if (
      referenceDetails?.hasAccountant ||
      accountantName ||
      referenceDetails?.emailAddress ||
      referenceDetails?.telephone
    ) {
      cards.push({
        id: "accountant-reference",
        kind: "accountant",
        data: referenceDetails,
        name:
          referenceDetails?.accountantReferenceName ||
          accountantName ||
          "Accountant",
        role: "Accountant Reference",
        email: referenceDetails?.emailAddress || "",
        phone: referenceDetails?.telephone || "",
        status: getReferenceStatus(
          referenceDetails?.referenceStatus || "awaiting-adviser",
        ),
      });
    }

    const tradeRefereeEntries = Array.isArray(referenceDetails?.tradeReferees)
      ? referenceDetails.tradeReferees.map((referee, index) => [
          String(index),
          referee,
        ])
      : Object.entries(referenceDetails?.tradeReferees || {});

    tradeRefereeEntries.forEach(([sourceKey, referee], index) => {
      if (!referee || !Object.values(referee).some(Boolean)) return;
      cards.push({
        id: `trade-reference-${sourceKey}`,
        kind: "trade",
        sourceKey,
        data: referee,
        name: referee.tradeName || referee.companyName || `Trade Referee ${index + 1}`,
        role: "Trade Reference",
        email: referee.tradeEmailAddress || "",
        phone: referee.refereePhoneNumber || "",
        status: getReferenceStatus(
          referee?.referenceStatus || "awaiting-adviser",
        ),
      });
    });

    const personalRefereeEntries = Array.isArray(
      referenceDetails?.personalReferees,
    )
      ? referenceDetails.personalReferees.map((referee, index) => [
          String(index),
          referee,
        ])
      : Object.entries(referenceDetails?.personalReferees || {});

    personalRefereeEntries.forEach(([sourceKey, referee], index) => {
      if (!referee || !Object.values(referee).some(Boolean)) return;
      cards.push({
        id: `personal-reference-${sourceKey}`,
        kind: "personal",
        sourceKey,
        data: referee,
        name: referee.refereeName || `Personal Referee ${index + 1}`,
        role: "Personal Reference",
        email: referee.refereeEmailAddress || "",
        phone: referee.refereePhoneNumber || "",
        status: getReferenceStatus(
          referee?.referenceStatus || "awaiting-adviser",
        ),
      });
    });

    const educationalRefereeEntries = Array.isArray(
      referenceDetails?.educationalReferees,
    )
      ? referenceDetails.educationalReferees.map((referee, index) => [
          String(index),
          referee,
        ])
      : Object.entries(referenceDetails?.educationalReferees || {});

    educationalRefereeEntries.forEach(([sourceKey, referee], index) => {
      if (!referee || !Object.values(referee).some(Boolean)) return;
      cards.push({
        id: `educational-reference-${sourceKey}`,
        kind: "educational",
        sourceKey,
        data: referee,
        name:
          referee.educationDetails ||
          referee.institutionName ||
          `Educational Reference ${index + 1}`,
        role: "Educational Reference",
        email: referee.educationalEmailAddress || referee.emailAddress || "",
        status: getReferenceStatus(
          referee?.referenceStatus || "awaiting-adviser",
        ),
      });
    });

    return cards;
  }, [referenceDetails]);
  const referenceCards = [...referenceHistories, ...additionalReferenceCards];
  const activeHistoryId = selectedHistoryId || referenceCards[0]?.id;
  const selectedWorkHistory = referenceCards.find(
    (reference) =>
      reference.id === activeHistoryId && reference.kind === "work-history",
  );
  const selectedPersonalReference = referenceCards.find(
    (reference) =>
      reference.id === activeHistoryId && reference.kind === "personal",
  );
  const selectedTradeReference = referenceCards.find(
    (reference) =>
      reference.id === activeHistoryId && reference.kind === "trade",
  );
  const selectedAccountantReference = referenceCards.find(
    (reference) =>
      reference.id === activeHistoryId && reference.kind === "accountant",
  );
  const selectedEducationalReference = referenceCards.find(
    (reference) =>
      reference.id === activeHistoryId && reference.kind === "educational",
  );
  const emptyWorkHistory = useMemo(
    () => ({
      regulatedRole: newReferenceType === "regulated",
    }),
    [newReferenceType],
  );
  const personalRefereeSourceKey = Array.isArray(
    referenceDetails?.personalReferees,
  )
    ? String(referenceDetails.personalReferees.length)
    : newReferenceId;
  const tradeRefereeSourceKey = Array.isArray(referenceDetails?.tradeReferees)
    ? String(referenceDetails.tradeReferees.length)
    : newReferenceId;
  const educationalRefereeSourceKey = Array.isArray(
    referenceDetails?.educationalReferees,
  )
    ? String(referenceDetails.educationalReferees.length)
    : newReferenceId;

  const beginAddingReference = () => {
    setSelectedHistoryId("__new-reference__");
    setNewReferenceType("");
    setNewReferenceId(`reference-${Date.now()}`);
    setIsAddingReference(true);
  };

  const selectReferenceCard = (referenceId) => {
    setIsAddingReference(false);
    setNewReferenceType("");
    setNewReferenceId("");
    setSelectedHistoryId(referenceId);
  };

  const finishAddingReference = (referenceId) => {
    setIsAddingReference(false);
    setNewReferenceType("");
    setSelectedHistoryId(referenceId);
  };

  const cancelAddingReference = () => {
    setIsAddingReference(false);
    setNewReferenceType("");
    setNewReferenceId("");
    setSelectedHistoryId(referenceCards[0]?.id || "");
  };

  return (
    <div className="main-content referencing-workspace-shell">
      <Helmet>
        <title>{applicantName} | Referencing</title>
      </Helmet>

      <Sidebar isSetExpanded />

      <main className="Main-Container referencing-workspace flex-1 gap-m">
        <Breadcrumb
          items={[
            { label: "Recruitment Applications", to: "/Applications" },
            { label: "Referencing" },
          ]}
        />

        {loading && (
          <div className="Content">Loading referencing application…</div>
        )}
        {/* {error && <div className="referencing-workspace-error">{error}</div>} */}

        {!loading && !error && (
          <>
            <section className="referencing-workspace-panel gap-m work-history-panel bg-white p-s">
              <div className="work-history-heading row-space-between gap-m">
                <div className="FP_Subtitle_Semibold text-primary-1">
                  {applicantName}
                </div>
                {/* <span className="Content-Small">
                  Showing the latest six years. Scroll horizontally for earlier
                  history.
                </span> */}
                <div className="align-center display-flex gap-m">
                  <div className="referencing-workspace-contact FP_Help_Text text-black">
                    {!!contactNumbers.length && (
                      <div className="referencing-workspace-phone-numbers row-wrap">
                        {contactNumbers.map((number, index) => (
                          <span key={`${number}-${index}`}>
                            {index > 0 && (
                              <span className="contact-number-separator">
                                |
                              </span>
                            )}
                            <a
                              href={`tel:${String(number).replace(/[^\d+]/g, "")}`}
                            >
                              {formatUKPhone(number)}
                            </a>
                          </span>
                        ))}
                      </div>
                    )}
                    {email && <a href={`mailto:${email}`}>{email}</a>}
                    {nationalInsuranceNumber && (
                      <span>NI: {nationalInsuranceNumber}</span>
                    )}
                  </div>
                  <EditIcon
                    className="referencing-contact-edit-icon"
                    aria-label="Edit applicant details"
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsApplicantDetailsOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        setIsApplicantDetailsOpen(true);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={`gantt-collapse-button ${
                      isGanttCollapsed ? "gantt-collapse-button--collapsed" : ""
                    }`}
                    onClick={() => setIsGanttCollapsed((collapsed) => !collapsed)}
                    aria-expanded={!isGanttCollapsed}
                    aria-label={
                      isGanttCollapsed
                        ? "Expand work history chart"
                        : "Collapse work history chart"
                    }
                  >
                    <ArrowDownIcon />
                  </button>
                </div>
              </div>
              {!isGanttCollapsed && (
                <WorkHistoryGantt
                  workHistory={workHistory}
                  onHistorySelect={selectReferenceCard}
                  selectedHistoryId={activeHistoryId}
                />
              )}
              {/* <div className="referencing-statuses display-flex flex-wrap gap-s">
                {RECRUITMENT_REFERENCE_STATUSES.map((status) => (
                  <OutlinedStatusButton
                    key={status.id}
                    label={status.label}
                    color={status.color}
                  />
                ))}
              </div> */}
            </section>

            <section className="referencing-workspace-panel">
              <button
                type="button"
                className="helpful-contacts-button"
                onClick={() => setIsHelpfulContactsOpen(true)}
                aria-label="View helpful contact details"
                title="Helpful contact details"
              >
                <HelpIcon />
              </button>
              <div className="reference-workspace-detail-layout row gap-m">
                <div className="reference-history-list p-xs">
                <div className="fit-column gap-m px-xxs">
                  {referenceCards.map((history) => {
                    const isActive = history.id === activeHistoryId;
                    const statusColor =
                      OUTLINED_STATUS_BUTTON_COLORS[history.status.color] ||
                      OUTLINED_STATUS_BUTTON_COLORS.grey;

                    return (
                      <button
                        type="button"
                        className={`reference-history-card bg-white corner ${
                          isActive ? "reference-history-card--active" : ""
                        }`}
                        style={{
                          "--reference-card-status-color": statusColor.border,
                        }}
                        key={history.id}
                        onClick={() => selectReferenceCard(history.id)}
                      >
                        <div className="flex-start-column gap-s flex-1 p-s">
                          <div className="FP_Content_Semibold text-black">
                            {history.name}
                          </div>
                          <div className="FP_Help_Text_Semibold text-primary-1">
                            {history.role}
                          </div>
                          {/* {(history.email || history.phone) && (
                            <div className="reference-history-card-contact FP_Help_Text">
                              {history.email && (
                                <a href={`mailto:${history.email}`}>
                                  {history.email}
                                </a>
                              )}
                              {history.phone && (
                                <a
                                  href={`tel:${String(history.phone).replace(/[^\d+]/g, "")}`}
                                >
                                  {formatUKPhone(history.phone)}
                                </a>
                              )}
                            </div>
                          )} */}
                          <OutlinedStatusButton
                            label={history.status.label}
                            color={history.status.color}
                            className="reference-history-card-status"
                          />
                        </div>
                      </button>
                    );
                  })}
                  <ButtonTextLink
                    label="Add Reference"
                    onClick={beginAddingReference}
                  />
                </div>
                </div>
                {isAddingReference && (
                  <div className="add-reference-creation fit-column gap-m flex-1">
                  <div className="work-history-reference-form add-reference-type-selector fit-column gap-m bg-white p-m corner card-corner-radius">
                    <div className="FP_Body_Semibold text-black">
                      Add Reference
                    </div>
                    <CustomSelect
                      label="Type of Reference"
                      options={NEW_REFERENCE_TYPE_OPTIONS}
                      value={newReferenceType}
                      onChange={(event) =>
                        setNewReferenceType(event.target.value)
                      }
                      placeholder="Select reference type"
                      clearable={false}
                      searchable={false}
                    />
                  </div>
                {isAddingReference &&
                  ["regulated", "non-regulated"].includes(
                    newReferenceType,
                  ) && (
                    <WorkHistoryReferenceForm
                      {...referenceEmailGuardProps}
                      history={emptyWorkHistory}
                      applicantName={applicantName}
                      nationalInsuranceNumber={nationalInsuranceNumber}
                      applicationId={adviserId}
                      referenceId={newReferenceId}
                      isNewReference
                      onCancel={cancelAddingReference}
                      referenceConsentAttachment={
                        applicant?.referenceConsentAttachment
                      }
                      onSaved={(referenceId, savedHistory) => {
                        setWorkHistory((current) => ({
                          ...current,
                          [referenceId]: savedHistory,
                        }));
                        finishAddingReference(referenceId);
                      }}
                    />
                  )}
                {isAddingReference && newReferenceType === "personal" && (
                  <PersonalReferenceForm
                    {...referenceEmailGuardProps}
                    referee={{}}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={newReferenceId}
                    sourceKey={personalRefereeSourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    onCancel={cancelAddingReference}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(personalReferees) => {
                      setReferenceDetails((current) => ({
                        ...current,
                        personalReferees,
                      }));
                      finishAddingReference(
                        `personal-reference-${personalRefereeSourceKey}`,
                      );
                    }}
                  />
                )}
                {isAddingReference && newReferenceType === "trade" && (
                  <TradeReferenceForm
                    {...referenceEmailGuardProps}
                    referee={{}}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={newReferenceId}
                    sourceKey={tradeRefereeSourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    onCancel={cancelAddingReference}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(tradeReferees) => {
                      setReferenceDetails((current) => ({
                        ...current,
                        tradeReferees,
                      }));
                      finishAddingReference(
                        `trade-reference-${tradeRefereeSourceKey}`,
                      );
                    }}
                  />
                )}
                {isAddingReference && newReferenceType === "educational" && (
                  <EducationalReferenceForm
                    {...referenceEmailGuardProps}
                    referee={{}}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={newReferenceId}
                    sourceKey={educationalRefereeSourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    onCancel={cancelAddingReference}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(educationalReferees) => {
                      setReferenceDetails((current) => ({
                        ...current,
                        educationalReferees,
                      }));
                      finishAddingReference(
                        `educational-reference-${educationalRefereeSourceKey}`,
                      );
                    }}
                  />
                )}
                {isAddingReference && newReferenceType === "accountant" && (
                  <AccountantReferenceForm
                    {...referenceEmailGuardProps}
                    referee={{}}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={newReferenceId}
                    sourceKey="accountant"
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    onCancel={cancelAddingReference}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(accountantDetails) => {
                      setReferenceDetails(accountantDetails);
                      finishAddingReference("accountant-reference");
                    }}
                  />
                )}
                  </div>
                )}
                {selectedWorkHistory && (
                  <WorkHistoryReferenceForm
                    {...referenceEmailGuardProps}
                    history={selectedWorkHistory.data}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={selectedWorkHistory.id}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(referenceId, savedHistory) =>
                      setWorkHistory((current) => ({
                        ...current,
                        [referenceId]: savedHistory,
                      }))
                    }
                  />
                )}
                {selectedPersonalReference && (
                  <PersonalReferenceForm
                    {...referenceEmailGuardProps}
                    referee={selectedPersonalReference.data}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={selectedPersonalReference.id}
                    sourceKey={selectedPersonalReference.sourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(personalReferees) =>
                      setReferenceDetails((current) => ({
                        ...current,
                        personalReferees,
                      }))
                    }
                  />
                )}
                {selectedTradeReference && (
                  <TradeReferenceForm
                    {...referenceEmailGuardProps}
                    referee={selectedTradeReference.data}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={selectedTradeReference.id}
                    sourceKey={selectedTradeReference.sourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(tradeReferees) =>
                      setReferenceDetails((current) => ({
                        ...current,
                        tradeReferees,
                      }))
                    }
                  />
                )}
                {selectedAccountantReference && (
                  <AccountantReferenceForm
                    {...referenceEmailGuardProps}
                    referee={selectedAccountantReference.data}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={selectedAccountantReference.id}
                    sourceKey="accountant"
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(accountantDetails) =>
                      setReferenceDetails(accountantDetails)
                    }
                  />
                )}
                {selectedEducationalReference && (
                  <EducationalReferenceForm
                    {...referenceEmailGuardProps}
                    referee={selectedEducationalReference.data}
                    applicantName={applicantName}
                    nationalInsuranceNumber={nationalInsuranceNumber}
                    applicationId={adviserId}
                    referenceId={selectedEducationalReference.id}
                    sourceKey={selectedEducationalReference.sourceKey}
                    referenceDetails={referenceDetails}
                    hasSixYearsEmploymentHistory={hasSixYearsEmploymentHistory}
                    hasCurrentEmployment={hasCurrentEmployment}
                    referenceConsentAttachment={
                      applicant?.referenceConsentAttachment
                    }
                    onSaved={(educationalReferees) =>
                      setReferenceDetails((current) => ({
                        ...current,
                        educationalReferees,
                      }))
                    }
                  />
                )}
              </div>
            </section>
          </>
        )}
      </main>
      <ApplicantReferenceDetailsModal
        open={isApplicantDetailsOpen}
        onClose={() => setIsApplicantDetailsOpen(false)}
        adviserId={adviserId}
        applicant={applicant}
        applicantName={applicantName}
        onConsentUpdated={(referenceConsentAttachment) =>
          setApplicant((current) => ({
            ...current,
            referenceConsentAttachment,
          }))
        }
        onDetailsUpdated={(details) =>
          setApplicant((current) => ({ ...current, ...details }))
        }
      />
      <HelpfulContactsModal
        open={isHelpfulContactsOpen}
        onClose={() => setIsHelpfulContactsOpen(false)}
      />
    </div>
  );
};

export default ReferencingWorkspacePage;
